import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { FaUserPlus, FaTrash, FaUserShield, FaSpinner } from "react-icons/fa";
import { getRoleUsers, assignUserRoles, removeUserRole } from "../services/rbacservice";
import { getUsers } from "../services/userservice";
import Modal from "../modal/modal";
import Dropdown from "../modal/dropdown";
import "./role_management.css";

export default function RoleUsersModal({ role, onClose, onUpdate }) {
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const USER_PAGE_SIZE = 5;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, assignedRes] = await Promise.all([
        getUsers({ page_size: 500 }),
        getRoleUsers(role.id)
      ]);
      const usersList = Array.isArray(usersRes) ? usersRes : (usersRes.data || usersRes.results || []);
      setAllUsers(usersList);
      const assignedList = Array.isArray(assignedRes) ? assignedRes : (assignedRes.data || assignedRes.results || []);
      setAssignedUsers(assignedList);
    } catch (err) {
      toast.error("Failed to load users for this role");
    } finally {
      setLoading(false);
    }
  }, [role?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAssignUser = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return;
    setAssigning(true);
    try {
      await assignUserRoles(parseInt(selectedUserId), [role.id]);
      toast.success("User successfully assigned to role");
      setSelectedUserId("");
      await loadData();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to assign user to role");
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveUser = async (user) => {
    const targetUserId = user.user || user.user_id || user.id;
    if (role.name.toLowerCase() === "super admin" && assignedUsers.length <= 1) {
      toast.warning("Cannot remove the last Super Admin!");
      return;
    }
    setRemovingId(user.id);
    try {
      await removeUserRole(targetUserId, role.id);
      toast.success(`Removed ${user.employee_name || user.full_name || user.user_name || user.email || user.user_email} from role ${role.name}`);
      await loadData();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Failed to remove user from role");
    } finally {
      setRemovingId(null);
    }
  };

  // Filter out users who are already assigned
  const assignedIds = new Set(assignedUsers.map(u => u.user || u.user_id || u.id));
  const availableUsers = allUsers.filter(u => !assignedIds.has(u.id));

  const filteredAssigned = assignedUsers.filter((user) => {
    if (!userSearch.trim()) return true;
    const name = (user.employee_detail?.full_name || user.employee_name || user.full_name || user.user_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || user.user_email || "").toLowerCase();
    const email = (user.email || user.user_email || "").toLowerCase();
    const q = userSearch.toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  const totalUserPages = Math.ceil(filteredAssigned.length / USER_PAGE_SIZE) || 1;
  const paginatedAssigned = filteredAssigned.slice((userPage - 1) * USER_PAGE_SIZE, userPage * USER_PAGE_SIZE);

  const userOptions = availableUsers.map((u) => ({
    label: `${u.employee_detail?.full_name || u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || u.email}${u.email ? ` (${u.email})` : ""}`,
    value: u.id,
  }));

  return (
    <Modal
      title={`Assigned Users — ${role.name}`}
      subtitle="Assign users to this role or remove currently assigned users."
      icon={<FaUserShield />}
      onClose={onClose}
      size="lg"
    >
      <div className="role-users-form-body">
        <div className="form-section-card">
          <div className="section-title-premium">
            <FaUserPlus /> Assign User
          </div>

          <form onSubmit={handleAssignUser} className="role-user-assign-row">
            <div className="role-user-dropdown">
              <Dropdown
                options={userOptions}
                value={selectedUserId}
                placeholder="Select a user to assign"
                onChange={(value) => setSelectedUserId(value)}
                disabled={assigning || loading}
              />
            </div>

            <button
              type="submit"
              className="primary role-user-assign-btn"
              disabled={!selectedUserId || assigning || loading}
            >
              {assigning ? <FaSpinner className="spin" /> : <FaUserPlus />}
              {assigning ? "Assigning..." : "Assign"}
            </button>
          </form>
        </div>

        <div className="form-section-card">
          <div className="section-title-premium role-users-title">
            <FaUserShield /> Currently Assigned ({filteredAssigned.length})
          </div>

          <div className="role-users-search-wrap">
            
            <input
              type="text"
              placeholder="🔍 Filter assigned users..."
              value={userSearch}
              onChange={(e) => {
                setUserSearch(e.target.value);
                setUserPage(1);
              }}
              className="role-users-search"
            />
          </div>

          {loading ? (
            <div className="role-users-state">
              <FaSpinner className="spin" />
              <span>Loading assigned users...</span>
            </div>
          ) : filteredAssigned.length === 0 ? (
            <div className="role-users-empty">
              <FaUserShield className="role-users-empty-icon" />
              <p>
                {userSearch
                  ? `No users matching "${userSearch}"`
                  : "No users assigned to this role yet."}
              </p>
              {!userSearch && (
                <span>Use the dropdown above to assign someone.</span>
              )}
            </div>
          ) : (
            <div className="role-users-list">
              {paginatedAssigned.map((user) => {
                const displayName =
                  user.employee_detail?.full_name ||
                  user.employee_name ||
                  user.full_name ||
                  user.user_name ||
                  `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                  user.email ||
                  user.user_email;

                return (
                  <div key={user.id} className="role-user-item">
                    <div className="role-user-info">
                      <div className="role-user-name">{displayName}</div>
                      <div className="role-user-meta">
                        {user.email || user.user_email || "No email"}
                        {user.phone ? ` • ${user.phone}` : ""}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveUser(user)}
                      disabled={removingId === user.id}
                      title="Remove from role"
                      className="role-user-remove-btn"
                    >
                      {removingId === user.id ? (
                        <FaSpinner className="spin" />
                      ) : (
                        <FaTrash />
                      )}
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {totalUserPages > 1 && (
            <div className="role-users-pagination">
              <span>
                Page {userPage} of {totalUserPages}
              </span>

              <div className="role-users-pagination-actions">
                <button
                  type="button"
                  onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                  disabled={userPage === 1}
                  className="secondary"
                >
                  Prev
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setUserPage((p) => Math.min(totalUserPages, p + 1))
                  }
                  disabled={userPage === totalUserPages}
                  className="secondary"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="modal-footer role-users-modal-footer">
        <div className="modal-actions">
          <button className="secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
